import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Bot, User, Loader2 } from 'lucide-react';
import { streamChat, IChatMessage } from '@/components/api/aiApi';
import { cn } from '@/lib/utils';

export function AI() {
    const [messages, setMessages] = useState<IChatMessage[]>([]);
    const [tokenConsumption, setTokenConsumption] = useState<number>(0);
    const [threadId, setThreadId] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessage: IChatMessage = { role: 'user', content: input.trim() };
        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setInput('');
        setIsLoading(true);

        let assistantContent = '';

        const updateAssistant = (chunk: string) => {
            assistantContent += chunk;
            setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                    return prev.map((message, index) => 
                        index === prev.length - 1 ? { ...message, content: assistantContent } : message
                    );
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
            });
        };

        console.log('Starting streamChat with messages:', newMessages);

        await streamChat(
            userMessage.content,
            threadId,
            updateAssistant,
            () => setIsLoading(false),
            (error) => {
                console.error('Chat error:', error);
                setMessages(prev => [
                    ...prev,
                    { role: 'assistant', content: `Error: ${error.message}` }
                ]);
                setIsLoading(false);
            },
            tokenConsumption,
            setTokenConsumption,
            (newThreadId) => setThreadId(newThreadId)
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-6rem)] max-w-4xl mx-auto p-4">
            <h1 className="text-2xl font-bold text-primary">AI Assistant</h1>
            <p className="text-sm text-gray-500 pb-2 mb-4">Tokens usage &nbsp;{tokenConsumption}</p>
            
            {/* Messages area */}
            <div className="flex-1 bg-card border border-border rounded-xl overflow-hidden flex flex-col">
                <ScrollArea className="flex-1 p-4">
                    {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                            <div className="text-center">
                                <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Start a conversation with the AI</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {messages.map((message, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        "flex gap-3 p-4 rounded-lg",
                                        message.role === 'user' 
                                            ? "bg-primary/10 ml-8" 
                                            : "bg-secondary/50 mr-8"
                                    )}
                                >
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                                        message.role === 'user' 
                                            ? "bg-primary text-primary-foreground" 
                                            : "bg-secondary text-secondary-foreground"
                                    )}>
                                        {message.role === 'user' ? (
                                            <User className="w-4 h-4" />
                                        ) : (
                                            <Bot className="w-4 h-4" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium mb-1">
                                            {message.role === 'user' ? 'You' : 'AI'}
                                        </p>
                                        <div className="text-sm whitespace-pre-wrap break-words">
                                            {message.content}
                                            {isLoading && 
                                             message.role === 'assistant' && 
                                             index === messages.length - 1 && (
                                                <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isLoading && messages[messages.length - 1]?.role === 'user' && (
                                <div className="flex gap-3 p-4 rounded-lg bg-secondary/50 mr-8">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-secondary text-secondary-foreground">
                                        <Bot className="w-4 h-4" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span className="text-sm text-muted-foreground">Thinking...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </ScrollArea>

                {/* Input area */}
                <div className="border-t border-border p-4">
                    <form onSubmit={handleSubmit} className="flex gap-2">
                        <Textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                            className="min-h-[60px] max-h-[200px] resize-none bg-background"
                            disabled={isLoading}
                        />
                        <Button 
                            type="submit" 
                            size="icon" 
                            className="h-[60px] w-[60px]"
                            disabled={!input.trim() || isLoading}
                        >
                            {isLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
